import { useState, type ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import { X } from 'lucide-react';
import { MAX_GALLERY_PHOTOS } from '@court-sync/shared';
import { Button, Field, FieldLabel } from './ui';

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export interface GalleryPhotoUploadProps {
  galleryUrls: string[];
  onChange: (urls: string[]) => void;
  onUpload: (file: File, index: string) => Promise<string>;
  inputId?: string;
  disabled?: boolean;
}

function validateImageFile(file: File): boolean {
  if (!file.type.startsWith('image/')) {
    toast.error(`"${file.name}" is not an image file`);
    return false;
  }
  if (file.size > MAX_FILE_SIZE) {
    toast.error(`"${file.name}" is too large (max 5MB before compression)`);
    return false;
  }
  return true;
}

export function GalleryPhotoUpload({
  galleryUrls,
  onChange,
  onUpload,
  inputId = 'gallery-upload',
  disabled = false,
}: GalleryPhotoUploadProps) {
  const [uploading, setUploading] = useState(false);

  const remaining = MAX_GALLERY_PHOTOS - galleryUrls.length;
  const canAddMore = remaining > 0;

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    if (galleryUrls.length >= MAX_GALLERY_PHOTOS) {
      toast.error(`You can upload up to ${MAX_GALLERY_PHOTOS} photos`);
      event.target.value = '';
      return;
    }

    const slotsAvailable = MAX_GALLERY_PHOTOS - galleryUrls.length;
    const filesToProcess = Array.from(files).slice(0, slotsAvailable);

    if (files.length > slotsAvailable) {
      toast.error(`Only ${slotsAvailable} more photo(s) can be added (max ${MAX_GALLERY_PHOTOS})`);
    }

    try {
      setUploading(true);
      const newUrls: string[] = [];

      for (let i = 0; i < filesToProcess.length; i++) {
        const file = filesToProcess[i];
        if (!validateImageFile(file)) continue;
        const index = `${galleryUrls.length + newUrls.length}-${Date.now()}`;
        const url = await onUpload(file, index);
        newUrls.push(url);
      }

      if (newUrls.length > 0) {
        onChange([...galleryUrls, ...newUrls]);
        toast.success(`Uploaded ${newUrls.length} photo${newUrls.length === 1 ? '' : 's'}`);
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload photos');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <Field>
        <FieldLabel htmlFor={inputId}>Gallery photos</FieldLabel>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleUpload}
            className="hidden"
            id={inputId}
            disabled={disabled || uploading || !canAddMore}
            multiple
          />
          <label
            htmlFor={inputId}
            className={`inline-flex w-fit cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${
              disabled || uploading || !canAddMore ? 'cursor-not-allowed opacity-50' : ''
            }`}
          >
            {uploading ? 'Uploading…' : 'Choose photos'}
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {galleryUrls.length}/{MAX_GALLERY_PHOTOS} photos · images are compressed to WebP
          </span>
        </div>
      </Field>

      {galleryUrls.length > 0 ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {galleryUrls.map((url, index) => (
            <div key={`${url}-${index}`} className="relative group">
              <img
                src={url}
                alt={`Gallery photo ${index + 1}`}
                className="h-24 w-full rounded-lg object-cover"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="absolute right-1 top-1 h-7 w-7 p-0"
                onClick={() => onChange(galleryUrls.filter((_, i) => i !== index))}
                aria-label={`Remove gallery photo ${index + 1}`}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
